import { computed, inject } from '@angular/core'

import {
  patchState,
  signalStore,
  type,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals'
import { addEntity, setAllEntities, updateEntity, withEntities } from '@ngrx/signals/entities'

import { catchError, endWith, lastValueFrom, map, of } from 'rxjs'

import { AiNodeData } from '@flowx/designer/modules/agent-builder/agent-builder/react/components/nodes/types/aiNode'
import {
  Agent,
  AgentDetails,
  AgentExecutionPlan,
  AgentInstance,
  AgentNode,
  AgentSummary,
  DuplicateAgentPayload,
  NodeExecution,
} from '@flowx/designer/modules/agent-builder/models/agent.entity'
import { AgentBuilderApi } from '@flowx/designer/modules/agent-builder/services/agent-builder.api'
import { AgentApiService } from '@flowx/designer/modules/agent-builder/services/agent.api'
import { DynamicClassifierApi } from '@flowx/designer/modules/agent-builder/services/dynamic-classifier.api'
import { KnowledgeBaseApi } from '@flowx/designer/modules/agent-builder/services/knoweledge-base.api'
import { Workflow } from '@flowx/designer/modules/integration-designer/models/workflow.entity'
import { ProxyWorkflowApi } from '@flowx/designer/modules/integration-designer/services/proxy-workflow.api'
import { AiWriterAmbientService } from '@flowx/designer/modules/new-ai/agents/writer/services/ai-writer-ambient.service'
import { OntologyApi } from '@flowx/designer/modules/ontology/services/ontology.api'
import { AppResourceList } from '@flowx/designer/shared/api/api-http-proxy.service'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'

type AgentDetailsStore = {
  agentDetails: Agent
  agentFlowxUuid: AgentDetails['flowxUuid']
  agentInstance: AgentInstance
  currentRunNodeId: string
  loading: boolean
  agentRunning: boolean
  nodeRunning: boolean
  isGeneratingAgent: boolean
  minimized: boolean
  systemResourceDefinitionIds: string[]
  showAddNewModal: boolean
  formSubmitted: boolean
  models: any[]
  modelsParser: any[]
  addons: any[]
  entities: any[]
  knowledgeBases: any[]
  ontologies: any[]
  dynamicClassifierDomains: any[]
  workflows: AppResourceList<Workflow[]>
  errors: any[]
  voiceCall: {
    workflowId: string
    voiceNodeId?: string | null
    applicationId?: string | null
  } | null
  dashboardSummary: any
  dashboardTimeSeries: any
  dashboardMetrics: any
  dashboardAlerts: any
  dashboardCost: any
  dashboardExecutions: any
  overallScore: any
}
const initialState: AgentDetailsStore = {
  agentDetails: null,
  agentInstance: null,
  agentFlowxUuid: null,
  currentRunNodeId: null,
  loading: false,
  nodeRunning: false,
  agentRunning: false,
  isGeneratingAgent: false,
  minimized: false,
  systemResourceDefinitionIds: [],
  showAddNewModal: false,
  formSubmitted: false,
  errors: [],
  models: [],
  modelsParser: [],
  addons: [],
  entities: [],
  knowledgeBases: [],
  ontologies: [],
  dynamicClassifierDomains: [],
  workflows: null,
  dashboardSummary: null,
  dashboardTimeSeries: null,
  dashboardMetrics: null,
  dashboardAlerts: null,
  dashboardCost: null,
  dashboardExecutions: null,
  overallScore: null,
  voiceCall: null,
}

// simple subscription list for non-Angular consumers (React) to observe minimized changes
const minimizedSubscribers = new Set<(v: boolean) => void>()

export const AgentDetailsStore = signalStore(
  withState(initialState),
  withEntities({ entity: type<AgentNode>(), collection: 'nodes' }),

  withEntities({ entity: type<AgentExecutionPlan>(), collection: 'executions' }),
  withMethods(
    (
      store,
      agentBuilderApiService = inject(AgentBuilderApi),
      agentApiService = inject(AgentApiService),
      knowledgeBaseApiService = inject(KnowledgeBaseApi),
      ontologyApiService = inject(OntologyApi),
      dynamicClassifierApiService = inject(DynamicClassifierApi),
      proxyWorkflowApiService = inject(ProxyWorkflowApi),
      urlParametersStoreService = inject(UrlParametersStoreService),
      ambientWriter = inject(AiWriterAmbientService)
    ) => ({
      setFormSubmitted: (submitted: boolean) => {
        patchState(store, { formSubmitted: submitted })
      },
      onShowDetailsNewModal: () => {
        patchState(store, { showAddNewModal: true })
      },
      onHideDetailsNewModal: () => {
        patchState(store, { showAddNewModal: false })
      },
      isGeneratingAgent: computed(() => store.isGeneratingAgent()),
      startGeneratingAgent: () => {
        patchState(store, { isGeneratingAgent: true })
      },
      stopGeneratingAgent: () => {
        patchState(store, { isGeneratingAgent: false })
      },

      update: async (agent: AgentSummary) => {
        const updatedAgent = await lastValueFrom(
          agentBuilderApiService.updateAgent({
            ...store.agentDetails(),
            ...agent,
          })
        )

        patchState(store, { agentDetails: { ...store.agentDetails(), ...updatedAgent } })
      },
      updateAgentLocal: async (updatedAgent: any) => {
        patchState(store, { agentDetails: { ...store.agentDetails(), ...updatedAgent } })
      },
      checkPrompt: async (prompt: string) => {
        const promptDoctor: any = await lastValueFrom(agentBuilderApiService.checkPrompt(prompt))
        return promptDoctor
      },
      generateSchema: async (request: string, currentSchema?: string) => {
        const schema: any = await lastValueFrom(
          agentBuilderApiService.generateSchema(request, currentSchema)
        )
        return schema
      },
      generateCode: async (payload: {
        query: string
        code_type: string
        current_code?: string
      }) => {
        const result: any = await lastValueFrom(agentBuilderApiService.generateCode(payload))
        return result
      },
      getModels: async () => store.models(),
      getModelsParser: async () => store.modelsParser(),
      getAddons: async (conversation: boolean) =>
        store.addons().filter((addon) => conversation || !addon.conversation_only),
      getEntities: async () => store.entities(),
      getKnowledgeBases: async () => store.knowledgeBases(),
      getOntologies: async () => store.ontologies(),
      getDynamicClassifierDomains: async () => store.dynamicClassifierDomains(),
      getWorkflows: async () => store.workflows(),
      getDashboardSummary: async () => store.dashboardSummary(),
      getDashboardTimeSeries: async () => store.dashboardTimeSeries(),
      getDashboardMetrics: async () => store.dashboardMetrics(),
      getDashboardCost: async () => store.dashboardCost(),
      getDashboardExecutions: async () => store.dashboardExecutions(),
      getOverallScore: async () => store.overallScore(),
      updateDefinition: async (agent: Agent) => {
        const updatedAgent: any = await lastValueFrom(agentBuilderApiService.updateAgent(agent))
        updatedAgent.nodes = updatedAgent.definition.nodes
        updatedAgent.edges = updatedAgent.definition.edges
        updatedAgent.viewport = updatedAgent.definition.viewport
        patchState(store, { agentDetails: { ...store.agentDetails(), ...updatedAgent } })
        if (updatedAgent?.id) {
          ambientWriter.notifyEntitySaved({
            entityType: 'agent',
            entityId: String(updatedAgent.id),
            entityName: updatedAgent.name ?? 'Agent',
            entityData: updatedAgent,
          })
        }
      },
      saveLocalAgent: async () => {
        const updatedAgent: any = await lastValueFrom(
          agentBuilderApiService.updateAgent({ ...store.agentDetails() })
        )
        patchState(store, {
          agentDetails: { ...store.agentDetails(), ...updatedAgent },
          isGeneratingAgent: false,
        })
        if (updatedAgent?.id) {
          ambientWriter.notifyEntitySaved({
            entityType: 'agent',
            entityId: String(updatedAgent.id),
            entityName: updatedAgent.name ?? 'Agent',
            entityData: updatedAgent,
          })
        }
      },
      duplicate: async (agent: DuplicateAgentPayload) =>
        await lastValueFrom(agentBuilderApiService.duplicateAgent(agent)),

      setMinimized() {
        patchState(store, { minimized: true })
        minimizedSubscribers.forEach((cb) => cb(true))
      },
      setMaximized() {
        patchState(store, { minimized: false })
        minimizedSubscribers.forEach((cb) => cb(false))
      },
      onMinimizedChange(cb: (v: boolean) => void) {
        minimizedSubscribers.add(cb)
        return () => {
          minimizedSubscribers.delete(cb)
        }
      },
      getGraphState: () => store.minimized(),
      getSavedAgent: async (resourceId: AgentDetails['id']) => {
        patchState(store, { loading: true })
        const agent: any = await lastValueFrom(agentBuilderApiService.getAgentById(resourceId))

        agent.nodes = agent.definition?.nodes || []
        agent.edges = agent.definition?.edges || []
        agent.viewport = agent.definition?.viewport || { zoom: 1, x: 0, y: 0 }
        agent.definition = {
          nodes: agent.nodes,
          edges: agent.edges,
          viewport: agent.viewport,
        }
        const models = await lastValueFrom(agentApiService.getModels())
        const modelsParser = await lastValueFrom(agentApiService.getModelsParser())
        const addons = await lastValueFrom(agentApiService.getAddons())
        const entities = await lastValueFrom(agentApiService.getEntities())
        const { appId } = urlParametersStoreService.urlParameters
        const knowledgeBases = await lastValueFrom(
          knowledgeBaseApiService.getKnowledgeBasesByApplicationId(appId)
        )
        const workflows = await lastValueFrom(proxyWorkflowApiService.getWorkflows())
        let ontologies: any[] = []
        try {
          // Ontologies are scoped by the FlowX application/project id (see
          // ontologies.component.ts), not the config/version id.
          ontologies = await lastValueFrom(ontologyApiService.listOntologies(appId))
        } catch {
          // ontology service may not be available
        }
        let dynamicClassifierDomains: any[] = []
        try {
          dynamicClassifierDomains = await lastValueFrom(dynamicClassifierApiService.listDomains())
        } catch {
          // dynamic-classifier service may not be available
        }

        patchState(store, {
          agentDetails: agent,
          agentFlowxUuid: agent.flowxUuid,
          loading: false,
          models,
          modelsParser,
          addons,
          knowledgeBases: knowledgeBases.items,
          ontologies,
          dynamicClassifierDomains,
          entities,
          workflows,
        })
        return agent
      },
      getDashboardData: async (
        granulary: 'hour' | 'day' | 'week' | 'month' | 'run',
        from: string,
        to: string,
        environment: 'DEV' | 'PROD'
      ) => {
        if (!store.agentDetails()?.id) {
          return
        }
        const dashboardSummary = await lastValueFrom(
          agentApiService.getAgentDashboardSummary(store.agentDetails().id, from, to, environment)
        )
        const dashboardTimeSeries = await lastValueFrom(
          agentApiService.getAgentDashboardTimeSeries(
            store.agentDetails().id,
            granulary,
            from,
            to,
            environment
          )
        )
        const dashboardMetrics = await lastValueFrom(
          agentApiService.getAgentDashboardMetrics(store.agentDetails().id, from, to, environment)
        )
        const dashboardAlerts = await lastValueFrom(
          agentApiService.getAgentDashboardAlerts(store.agentDetails().id, from, to, environment)
        )
        const dashboardCost = await lastValueFrom(
          agentApiService.getAgentDashboardCost(store.agentDetails().id, from, to, environment)
        )
        const dashboardExecutions = await lastValueFrom(
          agentApiService.getAgentDashboardExecutions(store.agentDetails().id, environment)
        )
        const overallScore = await lastValueFrom(
          agentApiService.getAgentOverallScore(
            store.agentDetails().id,
            granulary,
            from,
            to,
            environment
          )
        )
        patchState(store, {
          dashboardSummary,
          dashboardTimeSeries,
          dashboardMetrics,
          dashboardAlerts,
          dashboardCost,
          dashboardExecutions,
          overallScore,
        })
      },
      getSystemPrompt: async (query: any, nodeId: string) => {
        const system_prompt: any = await lastValueFrom(
          agentBuilderApiService.getSystemPrompt(query)
        )

        const updatedAgent = {
          ...store.agentDetails(),
          nodes: store.agentDetails().nodes.map((node) => {
            if (node.id === nodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  inputs: {
                    ...node.data.inputs,
                    system_prompt: system_prompt.generated_prompt,
                  },
                },
              }
            }
            return node
          }),
        }
        await lastValueFrom(
          agentBuilderApiService.updateAgent({ ...store.agentDetails(), ...updatedAgent })
        )
        patchState(store, {
          agentDetails: { ...store.agentDetails(), ...updatedAgent },
          isGeneratingAgent: false,
        })
        return system_prompt
      },
      getAdditionalPrompt: async (query: any) => {
        const system_prompt: any = await lastValueFrom(
          agentBuilderApiService.getSystemPrompt(query)
        )
        return system_prompt
      },
      getCurrentNodes: () => store.agentDetails()?.nodes,
      getCurrentEdges: () => store.agentDetails()?.edges,
      getCurrentViewPort: () => store.agentDetails()?.viewport,
      getAgentInputs: () => store.agentDetails()?.inputs,
      getAgentOutputs: () => store.agentDetails()?.outputs,
      getAgentExecutions: async (agentId: AgentDetails['id']) => {
        const executions: AgentExecutionPlan[] = await lastValueFrom(
          agentApiService.getAgentExecutions(agentId)
        )
        patchState(store, setAllEntities(executions, { collection: 'executions' }))
        return executions
      },
      addCurrentExecution: (execution: AgentExecutionPlan) => {
        patchState(store, addEntity(execution, { collection: 'executions' }))
      },
      updateCurrentExecution: (execution: AgentExecutionPlan) => {
        patchState(
          store,
          updateEntity(
            { id: execution.id, changes: { ...execution } },
            { collection: 'executions' }
          )
        )
      },
      getValidationErrros: () => store.errors(),
      updateLocalAgentExecution: (execution: AgentExecutionPlan) => {
        patchState(
          store,
          updateEntity(
            {
              id: execution.id,
              changes: {
                ...execution,
              },
            },
            { collection: 'executions' }
          )
        )
      },
      publishAgent: async (agentId: Agent['id']) =>
        await lastValueFrom(agentBuilderApiService.publishAgent(agentId)),
      runAgent: async (agent: Agent) => {
        patchState(store, { agentRunning: true })
        try {
          const agentExecutionPlan: AgentExecutionPlan = await lastValueFrom(
            agentBuilderApiService.runAgent(agent)
          )
          patchState(
            store,
            addEntity(agentExecutionPlan as AgentExecutionPlan, { collection: 'executions' })
          )
          patchState(store, { agentRunning: false })
          return agentExecutionPlan
        } catch (error) {
          patchState(store, { agentRunning: false })
        }
      },
      runAgentStream: (agent: Agent) =>
        agentBuilderApiService.runAgentStream(agent).pipe(
          map((response) => response),
          catchError((error) => of(error)),
          endWith(() => of(null))
        ),

      runNode: async (node_id: string, node_data: AiNodeData) => {
        patchState(store, { currentRunNodeId: node_id, nodeRunning: true })
        try {
          const { appId, appVersionId } = urlParametersStoreService.urlParameters
          if (!node_data.outputs) {
            node_data.outputs = {}
          }
          const nodeExecution: NodeExecution = await lastValueFrom(
            agentBuilderApiService.runNode(node_data, appId, appVersionId)
          )
          patchState(store, { currentRunNodeId: null, nodeRunning: false })
          return nodeExecution
        } catch (error) {
          patchState(store, { currentRunNodeId: null, agentRunning: false })
          return error
        }
      },

      // Voice agents open a live call panel (out-of-process Pipecat bot)
      // instead of a synchronous node run. React's NodeHeader calls this; the
      // Angular page renders <flx-agent-voice-call> off the voiceCall signal.
      startVoiceCall: (node_id: string) => {
        const agent = store.agentDetails()
        patchState(store, {
          voiceCall: {
            workflowId: agent?.id,
            voiceNodeId: node_id,
            applicationId: (agent as any)?.application_id ?? null,
          },
        })
      },
      closeVoiceCall: () => {
        patchState(store, { voiceCall: null })
      },

      getAudioContent: async (audioUrl: string) => {
        const audioContent: any = await lastValueFrom(
          agentBuilderApiService.getAudioContent(audioUrl)
        )
        return audioContent
      },

      getPdfContent: async (pdfUrl: string) => {
        const pdfContent: any = await lastValueFrom(agentBuilderApiService.getPdfContent(pdfUrl))
        return pdfContent
      },

      getImageContent: async (imageUrl: string) => {
        const imageContent: any = await lastValueFrom(
          agentBuilderApiService.getImageContent(imageUrl)
        )
        return imageContent
      },

      setCurrentRunNodeId: (nodeId: string) => {
        patchState(store, { currentRunNodeId: nodeId })
      },

      cancelRun: () => {
        patchState(store, { agentRunning: false, nodeRunning: false })
      },

      updateLocalNode: (update: { id: AgentNode['id'] }) => {
        patchState(
          store,
          updateEntity({ id: update.id, changes: { ...update } }, { collection: 'nodes' })
        )
      },

      setNodeRunning: (isRunning: boolean, nodeId: string) => {
        patchState(store, { nodeRunning: isRunning, currentRunNodeId: nodeId })
      },

      setValidationErrors(errors) {
        patchState(store, { errors })
      },
      clearValidationErrors() {
        patchState(store, { errors: [] })
      },
    })
  ),
  withComputed((store) => ({
    agentId: computed(() => store.agentDetails()?.id),
    agentResourceId: computed(() => store.agentDetails()?.id),
    agentName: computed(() => store.agentDetails()?.name),
    agentDescription: computed(() => store.agentDetails()?.description),
    agentCode: computed(() => store.agentDetails()?.code),
    agentInputs: computed(() => store.agentDetails()?.inputs),
    agentOutputs: computed(() => store.agentDetails()?.outputs),
    executions: computed(() =>
      store.executionsEntities().sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
    ),
  })),
  withHooks({
    async onInit() {
      // const models = await lastValueFrom(agentApiService.getModels())
      // patchState(store, {
      //   models,
      // })
    },
  })
)
